import React from 'react';
import { X, Activity, Leaf, Eye, Volume2, Wind, Hand, Clock, Info, Check, Sparkles, Save, Calendar } from 'lucide-react';
import { NatureIntegrationDetail as NatureDetailType, SingleDosha } from '../types';
import { PoseImage } from './PoseImage';

interface NatureIntegrationDetailProps {
  data: NatureDetailType;
  onClose: () => void;
  activeDosha: string;
  imageUrl?: string | null;
}

export const NatureIntegrationDetail: React.FC<NatureIntegrationDetailProps> = ({ data, onClose, activeDosha, imageUrl }) => {
  const userBaseDosha = activeDosha.split('-')[0] as SingleDosha;
  const fallbackImage = `/images/yoga/nature-${userBaseDosha}.png`;
  const finalImage = imageUrl || fallbackImage;

  const sensory = data.sensoryPractice || { sight: '', sound: '', smell: '', touch: '' };
  const guidance = data.doshaGuidance || { 
    vata: { environment: '', timing: '', avoidance: '' }, 
    pitta: { environment: '', timing: '', avoidance: '' }, 
    kapha: { environment: '', timing: '', avoidance: '' } 
  };
  const timing = data.optimalTiming || { vata: '', pitta: '', kapha: '' };

  const getDoshaColor = (dosha: string) => {
    switch (dosha) {
      case 'vata': return 'text-orange-600 bg-orange-50 border-orange-100';
      case 'pitta': return 'text-blue-600 bg-blue-50 border-blue-100';
      case 'kapha': return 'text-emerald-600 bg-emerald-50 border-emerald-100';
      default: return 'text-gray-600 bg-gray-50 border-gray-100';
    }
  };

  const isDoshaActive = (dosha: string) => activeDosha.includes(dosha);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden relative shadow-2xl flex flex-col md:flex-row shadow-sage-900/20">
        <button onClick={onClose} className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full z-20 hover:bg-black/70 transition-colors shadow-lg">
          <X size={20} />
        </button>

        {/* Left Column: Hero & Visuals */}
        <div className="w-full md:w-5/12 bg-sage-50 flex flex-col overflow-hidden">
          <div className="h-48 md:h-64 relative overflow-hidden bg-sage-200">
            {finalImage ? (
              <img 
                src={finalImage} 
                alt={data.activityName} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = fallbackImage;
                }}
              />
            ) : (
              <div className="w-full h-full bg-[#2D6A4F]/15 flex items-center justify-center">
                <Leaf size={48} className="text-[#2D6A4F]" />
              </div>
            )}
            <span className="absolute bottom-4 left-4 text-[10px] font-black uppercase text-white tracking-widest bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20">Nature Rx</span>
          </div>

          <div className="p-8 flex-1 overflow-y-auto bg-sage-900 text-white">
            <h2 className="text-3xl font-serif font-bold mb-4">{data.activityName || 'Nature Practice'}</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-[#2D6A4F] pl-4 py-1 italic text-sage-100 text-sm leading-relaxed">
                "{data.benefit || 'Enjoy the healing energy of the natural world.'}"
                <div className="mt-4">
                   <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#2D6A4F]/20 text-[#2D6A4F] text-[10px] font-bold uppercase tracking-wider border border-[#2D6A4F]/30">
                    <Leaf size={10} /> {data.textualSource || 'Classical Ayurvedic Principles'}
                  </span>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-sage-400 mb-4 flex items-center gap-2">
                  <Hand size={12} className="text-indigo-400" /> Sensory Awareness
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1.5 text-sage-300">
                      <Eye size={10} /> <span className="text-[9px] font-bold uppercase tracking-tighter">Sight</span>
                    </div>
                    <p className="text-[11px] text-sage-100 line-clamp-3">{sensory.sight}</p>
                  </div>
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1.5 text-sage-300">
                      <Volume2 size={10} /> <span className="text-[9px] font-bold uppercase tracking-tighter">Sound</span>
                    </div>
                    <p className="text-[11px] text-sage-100 line-clamp-3">{sensory.sound}</p>
                  </div>
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1.5 text-sage-300">
                      <Wind size={10} /> <span className="text-[9px] font-bold uppercase tracking-tighter">Smell</span>
                    </div>
                    <p className="text-[11px] text-sage-100 line-clamp-3">{sensory.smell}</p>
                  </div>
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1.5 text-sage-300">
                      <Hand size={10} /> <span className="text-[9px] font-bold uppercase tracking-tighter">Touch</span>
                    </div>
                    <p className="text-[11px] text-sage-100 line-clamp-3">{sensory.touch}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Prescription & Sequence */}
        <div className="w-full md:w-7/12 p-8 md:p-12 overflow-y-auto flex flex-col bg-white">
          <div className="mb-8">
            <h3 className="font-black text-sage-900 mb-6 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
              <Activity className="text-[#2D6A4F]" size={16} strokeWidth={3} /> Dosha Prescription
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(['vata', 'pitta', 'kapha'] as const).map((dosha) => {
                const isActive = isDoshaActive(dosha);
                const info = guidance[dosha];
                return (
                  <div key={dosha} className={`p-4 rounded-2xl border-2 transition-all ${isActive ? 'border-[#2D6A4F] bg-sage-50/50 shadow-sm' : 'border-gray-50 opacity-60'}`}>
                    <div className="flex items-center justify-between mb-2">
                       <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${getDoshaColor(dosha)}`}>
                        {dosha}
                      </span>
                    </div>
                    <p className="text-[11px] font-bold text-sage-900 mb-1 leading-tight">{info?.environment}</p>
                    <p className="text-[10px] text-sage-600 mb-2 truncate italic">{info?.timing}</p>
                    <div className="pt-2 border-t border-sage-100">
                      <p className="text-[9px] text-red-500 font-bold uppercase tracking-tighter">Avoid</p>
                      <p className="text-[10px] text-red-700 leading-tight">{info?.avoidance}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex-1 mb-8">
            <h3 className="font-black text-sage-900 mb-4 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
              <Clock className="text-[#2D6A4F]" size={16} strokeWidth={3} /> Practice Sequence
            </h3>
            <div className="space-y-4">
              {(data.steps || []).map((step, idx) => (
                <div key={idx} className="flex gap-4 items-start group">
                  <div className="w-6 h-6 rounded-full bg-[#2D6A4F] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {step.stepNumber}
                  </div>
                  <div className="flex-1 pb-4 border-b border-sage-50 last:border-0">
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="text-sm font-bold text-sage-900">{step.title}</h4>
                      <span className="text-[10px] font-bold text-[#2D6A4F] uppercase tracking-tighter bg-sage-50 px-2 py-0.5 rounded-full">{step.duration}</span>
                    </div>
                    <p className="text-[12px] text-sage-600 leading-relaxed italic">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
             <h3 className="font-black text-sage-900 mb-4 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
              <Calendar className="text-[#2D6A4F]" size={16} strokeWidth={3} /> Optimal Timing
            </h3>
            <div className="grid grid-cols-3 gap-3">
               {(['vata', 'pitta', 'kapha'] as const).map((dosha) => (
                 <div key={dosha} className={`p-3 rounded-xl bg-sage-50 border border-sage-100 flex flex-col ${isDoshaActive(dosha) ? 'outline outline-2 outline-[#2D6A4F]' : 'opacity-60'}`}>
                    <span className="text-[9px] font-black uppercase text-sage-400 mb-1">{dosha}</span>
                    <span className="text-[11px] font-bold text-sage-900">{timing[dosha]}</span>
                 </div>
               ))}
            </div>
          </div>

          <div className="bg-[#FFF8F0] border border-[#FAC775] p-6 rounded-3xl mb-8 flex gap-4 items-start">
             <div className="bg-[#FAC775]/20 p-2 rounded-xl text-[#B7791F]">
                <Info size={18} />
             </div>
             <div>
                <h3 className="font-bold text-[#92400E] text-[10px] uppercase tracking-widest mb-1">Contraindications & Safety</h3>
                <p className="text-[#92400E] text-[12px] font-medium leading-relaxed italic">{data.contraindications}</p>
             </div>
          </div>

          {/* Removed Individual Action Buttons to avoid confusion with main page save */}
        </div>
      </div>
    </div>
  );
};
